/* eslint-disable no-console */
import { useEffect, useState } from 'react';
import { LiveComponent } from '@amzn/aws-tc-xb-xps';
import { ContentLayout } from '@amzn/awsui-components-react';
import { PostAuthComponentParams } from '../../types/auth';
import { AppConfig } from '../../types/app';
import { useApolloClient } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useIntl } from 'react-intl';
import { emitPostAuthRumEvent } from '../../services/emitPostAuthRumEvent';
import { PostAuthRumEvent } from '../../constants/metrics';
import configService from '../../services/config';
import GandalfFlashbar from '../GandalfFlashbar';

const PROFILE_STATUS = {
  COMPLETE: 'COMPLETE',
  SIMPLE: 'SIMPLE',
  MINIMAL: 'MINIMAL',
  INCOMPLETE: 'INCOMPLETE',
};

const COMPONENT_NAME = {
  COMPLETE: 'UserProfileEditAll',
  SIMPLE: 'SimplifiedProfile',
};

export const getAuthLink = (jwtToken: string) => {
  return setContext((_, { headers }) => {
    console.log('Setting auth header');
    console.log(headers);
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${jwtToken}`,
      },
    };
  });
};

function ProfileAndPreferences({
  config,
  onContinue,
  user,
}: PostAuthComponentParams) {
  const apolloClient = useApolloClient();
  const { locale } = useIntl();
  const [componentToRender, setComponentToRender] = useState(
    COMPONENT_NAME.COMPLETE
  );
  const isHcUatSignIn = configService.isHcUATSignIn();

  useEffect(() => {
    const listener = () => {
      emitPostAuthRumEvent(user, PostAuthRumEvent.PROFILE_COMPLETE);
      onContinue();
    };
    document.addEventListener('edit_profile_all_sections_success', listener);
    return () => {
      document.removeEventListener(
        'edit_profile_all_sections_success',
        listener
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (
      (config.enableSimpleProfileFeature === 'true' || isHcUatSignIn) &&
      user.profile_completion_status
    ) {
      console.log('Starting Simple profile flow');
      const requiredStatus = getClientRequiredStatus(config);

      if (!shouldShowProfile(user.profile_completion_status, requiredStatus)) {
        onContinue();
        return;
      }

      //If we didn't exit above, requiredStatus can only be SIMPLE or COMPLETE
      setComponentToRender(
        requiredStatus === PROFILE_STATUS.SIMPLE
          ? COMPONENT_NAME.SIMPLE
          : COMPONENT_NAME.COMPLETE
      );

      const postAuthRumEvent =
        requiredStatus === PROFILE_STATUS.SIMPLE
          ? PostAuthRumEvent.SIMPLE_PROFILE_RENDERED
          : PostAuthRumEvent.COMPLETE_PROFILE_RENDERED;
      emitPostAuthRumEvent(user, postAuthRumEvent);
    } else {
      console.log('Starting Complete profile flow');
      if (
        config.enableProfilePreferences !== 'true' ||
        user.profile_completion_required !== 'true' ||
        !shouldRenderProfileAndPreferencesForClient(config)
      ) {
        onContinue();
        return;
      }
      setComponentToRender(COMPONENT_NAME.COMPLETE);
      emitPostAuthRumEvent(user, PostAuthRumEvent.PROFILE_SHOWN);
    }
  }, [
    config,
    onContinue,
    user,
    user.profile_completion_required,
    user.profile_completion_status,
    isHcUatSignIn,
  ]);

  if (config.enableProfilePreferences === 'true') {
    console.debug('Setting link');
    console.debug(apolloClient);
    console.debug(apolloClient.link);
    apolloClient.setLink(getAuthLink(user.jwtToken!).concat(apolloClient.link));
    console.debug('Link set');
    console.debug(apolloClient.link);
  }

  console.log('current locale is,', locale);

  return (
    <ContentLayout>
      <GandalfFlashbar />
      <LiveComponent
        experienceProviderName="userorg"
        componentName={componentToRender}
        payload={JSON.stringify({
          token: user?.jwtToken,
          language: locale,
        })}
        data-testid="profile-live-component"
      />
    </ContentLayout>
  );
}

const getClientRequiredStatus = (config: AppConfig): string | undefined => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const clientId = urlSearchParams.get('client_id');
  return config.requiredProfileCompletionStatusForClients?.[clientId!];
};

const shouldShowProfile = (
  userStatus: string,
  requiredStatus: string | undefined
): boolean => {
  if (
    !isValidStatus(userStatus) ||
    !requiredStatus ||
    !isValidStatus(requiredStatus)
  )
    return false;
  if (requiredStatus === PROFILE_STATUS.MINIMAL) return false;
  if (userStatus === PROFILE_STATUS.COMPLETE) return false;
  if (requiredStatus === PROFILE_STATUS.COMPLETE) return true;

  return userStatus !== PROFILE_STATUS.SIMPLE; //requiredStatus can only be SIMPLE by this point
};

const isValidStatus = (status: string): boolean => {
  return Object.values(PROFILE_STATUS).includes(status as any);
};

const shouldRenderProfileAndPreferencesForClient = (
  config: AppConfig
): boolean => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const client = urlSearchParams.get('client_id');

  if (
    config.userProfileRequiredForClients === undefined ||
    config.userProfileRequiredForClients.length === 0
  )
    return false;

  return config.userProfileRequiredForClients.includes(client!);
};

export default ProfileAndPreferences;
